import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import BlockIcon from "@material-ui/icons/Block";
import WarningIcon from "@material-ui/icons/Warning";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import EditIcon from "@material-ui/icons/Edit";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import TimelineIcon from "@material-ui/icons/Timeline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DriverDetails from "./DriverDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { Avatar, Stars, assetUrl, readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The car and defaultCar sub-selections used to be spelled out twice,
 * field for field — the same 40 lines duplicated. They are one fragment
 * here, and the list only asks for what a row and the details dialog
 * actually read.
 * ------------------------------------------------------------------ */

const CAR_FIELDS = `
  _id
  plate
  ride
  delivery
  manufacturingYear
  registrationDocumentUrl
  description
  color {
    name
  }
  brand {
    name
  }
  model {
    name
  }
  carType {
    type
  }
  pictures {
    inner {
      url
    }
    outer {
      url
    }
  }
  insurance {
    insuranceImageUrl
    expireDate
  }
`;

const DRIVER_FILTERS = `
  _id: $_id
  fullName: $fullName
  phoneNumber: $phoneNumber
  state: $state
  gender: $gender
  isVerified: $isVerified
  IncompleteInformationDrivers: $IncompleteInformationDrivers
  averageRateFrom: $averageRateFrom
  averageRate: $averageRate
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const DRIVER_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $fullName: String
  $phoneNumber: String
  $state: DriverState
  $gender: String
  $isVerified: Boolean
  $IncompleteInformationDrivers: Boolean
  $averageRateFrom: Float
  $averageRate: Float
  $createdAtFrom: Date
  $createdAt: Date
  $fullNameSort: Int
  $averageRateSort: Int
  $createdAtSort: Int
`;

const GET_DRIVERS = gql`
  query getDriversByAdmin(${DRIVER_VARIABLES}) {
    getDriversByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${DRIVER_FILTERS} }
      sort: { fullName: $fullNameSort, averageRate: $averageRateSort, createdAt: $createdAtSort }
    ) {
      _id
      fullName
      phoneNumber
      email
      gender
      profileImageUrl
      workStatus
      state
      birthDate
      createdAt
      isVerified
      emailVerified
      phoneNumberVerified
      hasNotification
      stripeAccountId
      averageRate
      numberOfRates
      address {
        full
        zipCode
      }
      drivingLicence {
        photoUrl
        licenceId
        expireDate
      }
      car {
        ${CAR_FIELDS}
      }
      defaultCar {
        ${CAR_FIELDS}
      }
    }
    getDriversCountByAdmin(filters: { ${DRIVER_FILTERS} })
  }
`;

const GET_DRIVER_COUNTS = gql`
  query getDriverCounts {
    total: getDriversCountByAdmin(filters: {})
    active: getDriversCountByAdmin(filters: { state: ACTIVE })
    suspended: getDriversCountByAdmin(filters: { state: SUSPENDED })
    unverified: getDriversCountByAdmin(filters: { isVerified: false })
  }
`;

const SUSPEND_DRIVERS = gql`
  mutation suspendDriverByAdmin($idSet: [ID!]!) {
    suspendDriverByAdmin(idSet: $idSet) {
      _id
      state
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const VERIFIED_OPTIONS = [
  { value: "", label: "Any verification" },
  { value: "true", label: "Verified" },
  { value: "false", label: "Not verified" },
];
const VERIFIED_LABELS = VERIFIED_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "fullName", label: "Name" },
  { value: "phoneNumber", label: "Phone" },
];
const SEARCH_LABELS = { fullName: "Name", phoneNumber: "Phone" };

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "rateHigh", label: "Best rated" },
  { value: "rateLow", label: "Worst rated" },
  { value: "nameAsc", label: "Name A-Z" },
];

const FILTER_DEBOUNCE_MS = 350;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");

// A driver cannot legally work on an expired licence, and the expiry is
// otherwise buried in the details dialog.
const licenceState = (driver) => {
  const expiry = driver.drivingLicence && driver.drivingLicence.expireDate;
  if (!expiry) return null;
  const days = moment(expiry).diff(moment(), "days");
  if (days < 0) return { tone: "danger", label: "Licence expired" };
  if (days <= 30) return { tone: "warning", label: `Licence ${days}d` };
  return null;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Drivers = () => {
  const [searchTarget, setSearchTarget] = useState("fullName");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [rateFrom, setRateFrom] = useState("");
  const [rateTo, setRateTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [driverDetail, setDriverDetail] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        fullName: undefined,
        phoneNumber: undefined,
        ...(term ? { [searchTarget]: term } : {}),
        state: stateFilter || undefined,
        isVerified: verifiedFilter === "" ? undefined : verifiedFilter === "true",
        IncompleteInformationDrivers: incompleteOnly ? true : undefined,
        averageRateFrom: rateFrom ? parseFloat(rateFrom) : undefined,
        averageRate: rateTo ? parseFloat(rateTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        fullNameSort: sortOption === "nameAsc" ? 1 : undefined,
        averageRateSort:
          sortOption === "rateHigh" ? -1 : sortOption === "rateLow" ? 1 : undefined,
        createdAtSort:
          sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    stateFilter,
    verifiedFilter,
    incompleteOnly,
    rateFrom,
    rateTo,
    dateFrom,
    dateTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_DRIVERS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_DRIVER_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_DRIVERS, {
    fetchPolicy: "network-only",
  });
  const [suspendDrivers] = useMutation(SUSPEND_DRIVERS);

  const drivers = (data && data.getDriversByAdmin) || [];
  const totalCount = (data && data.getDriversCountByAdmin) || 0;
  const counts = countsData || {};
  const licenceIssuesOnPage = drivers.filter((d) => licenceState(d)).length;

  /* ---------------- selection + suspend ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = drivers.map((d) => d._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    drivers.length > 0 && selectedIds.length === drivers.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runSuspend = () => {
    const targets = suspendTarget || [];
    setWorking(true);
    setActionError("");
    suspendDrivers({ variables: { idSet: targets.map((d) => d._id) } })
      .then(() => {
        setWorking(false);
        setSuspendTarget(null);
        setDriverDetail(null);
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setWorking(false);
        setSuspendTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (stateFilter) activeFilterChips.push({ id: "state", label: `State: ${STATE_LABELS[stateFilter]}` });
  if (verifiedFilter) {
    activeFilterChips.push({ id: "verified", label: VERIFIED_LABELS[verifiedFilter] });
  }
  if (incompleteOnly) {
    activeFilterChips.push({ id: "incomplete", label: "Incomplete profile" });
  }
  if (rateFrom || rateTo) {
    activeFilterChips.push({ id: "rate", label: `Rating: ${rateFrom || "0"} to ${rateTo || "5"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Joined: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "state") setStateFilter("");
    if (id === "verified") setVerifiedFilter("");
    if (id === "incomplete") setIncompleteOnly(false);
    if (id === "rate") {
      setRateFrom("");
      setRateTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStateFilter("");
    setVerifiedFilter("");
    setIncompleteOnly(false);
    setRateFrom("");
    setRateTo("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Name", "Phone", "Email", "State", "Verified", "Rating", "Vehicle", "Licence expires", "Joined"],
    ];
    (exportData.getDriversByAdmin || []).forEach((d) => {
      const car = d.defaultCar || (d.car || [])[0];
      rows.push([
        d.fullName || "-",
        d.phoneNumber || "-",
        d.email || "-",
        STATE_LABELS[d.state] || d.state || "-",
        d.isVerified ? "yes" : "no",
        d.averageRate ? Number(d.averageRate).toFixed(1) : "-",
        car ? [car.brand && car.brand.name, car.model && car.model.name, car.plate].filter(Boolean).join(" ") : "-",
        d.drivingLicence && d.drivingLicence.expireDate
          ? moment(d.drivingLicence.expireDate).format("MM/DD/YYYY")
          : "-",
        moment(d.createdAt).format("MM/DD/YYYY"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Drivers");
    XLSX.writeFile(wb, "Drivers.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superPeoplePage superDriversPage">
        <main className="main">
          <PageHeader title="Drivers" description="Everyone driving for Spark." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedDrivers = drivers.filter((d) => selectedIds.includes(d._id));

  return (
    <div className="page superPeoplePage superDriversPage">
      <main className="main">
        <PageHeader
          title="Drivers"
          count={totalCount}
          description="Everyone driving for Spark, what they drive, and whether their paperwork is in order."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} driver${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<DirectionsCarIcon />} title="Matching drivers" value={totalCount} />
          <MetricCard
            icon={<VerifiedUserIcon />}
            title="Active"
            value={counts.active || 0}
            tone="success"
          />
          <MetricCard
            icon={<WarningIcon />}
            title="Not verified"
            value={counts.unverified || 0}
            tone={counts.unverified > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Suspended"
            value={counts.suspended || 0}
            tone={counts.suspended > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={stateFilter ? `State: ${STATE_LABELS[stateFilter]}` : "State"}
              options={[{ value: "", label: "Any state" }, ...STATE_OPTIONS]}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
            <FilterDropdown
              label={verifiedFilter ? VERIFIED_LABELS[verifiedFilter] : "Verification"}
              options={VERIFIED_OPTIONS}
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
            />
            <FilterDropdown
              label={incompleteOnly ? "Incomplete profile" : "Profile"}
              options={[
                { value: "", label: "Any profile" },
                { value: "incomplete", label: "Incomplete profile" },
              ]}
              value={incompleteOnly ? "incomplete" : ""}
              onChange={(e) => setIncompleteOnly(e.target.value === "incomplete")}
            />
            <NumberRangeFilter
              label="Rating"
              fromValue={rateFrom}
              toValue={rateTo}
              onFromChange={(e) => setRateFrom(e.target.value)}
              onToChange={(e) => setRateTo(e.target.value)}
            />
            <DateRangeFilter
              label="Joined"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              onClick={() => setSuspendTarget(selectedDrivers)}
            >
              Suspend selected
            </Button>
          }
          leftContent={
            licenceIssuesOnPage > 0 ? (
              <span className="superPersonPill">
                {licenceIssuesOnPage} licence issue{licenceIssuesOnPage === 1 ? "" : "s"} on this page
              </span>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh drivers"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superPeopleSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every driver on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Driver</div>
              <div className="tableHeaderCell">Contact</div>
              <div className="tableHeaderCell">Vehicle</div>
              <div className="tableHeaderCell">Rating</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {drivers.length === 0 ? (
              <EmptyState
                icon={<DirectionsCarIcon />}
                title={activeFilterChips.length === 0 ? "No drivers yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Drivers appear here once they sign up and are verified."
                    : "No drivers match these filters. Try widening or clearing them."
                }
              />
            ) : (
              drivers.map((driver) => {
                const car = driver.defaultCar || (driver.car || [])[0];
                const licence = licenceState(driver);
                return (
                  <div className="row" key={driver._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(driver._id)}
                      onChange={() => toggleSelectOne(driver._id)}
                      inputProps={{ "aria-label": `Select ${driver.fullName}` }}
                    />

                    <div className="superPersonCell" data-label="Driver">
                      <Avatar name={driver.fullName} url={assetUrl(driver.profileImageUrl)} />
                      <span className="superPersonText">
                        <span className="superPersonName">{driver.fullName || "Unnamed"}</span>
                        <span className="superPersonMeta">
                          Joined {formatDate(driver.createdAt)}
                        </span>
                      </span>
                    </div>

                    <div className="superPersonText" data-label="Contact">
                      <span className="superPersonName">{driver.phoneNumber || "No phone"}</span>
                      <span className="superPersonMeta">{driver.email || "No email"}</span>
                    </div>

                    <div className="superPersonText" data-label="Vehicle">
                      {car ? (
                        <React.Fragment>
                          <span className="superPersonName">
                            {[car.brand && car.brand.name, car.model && car.model.name]
                              .filter(Boolean)
                              .join(" ") || "Unspecified"}
                          </span>
                          <span className="superPersonMeta">{car.plate}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superPersonMuted">No vehicle</span>
                      )}
                    </div>

                    <span data-label="Rating">
                      <Stars value={driver.averageRate} />
                    </span>

                    <span className="superPersonStack" data-label="Status">
                      <StatusBadge
                        status={
                          driver.state === "SUSPENDED"
                            ? "danger"
                            : driver.isVerified
                            ? "success"
                            : "warning"
                        }
                        label={
                          driver.state === "SUSPENDED"
                            ? "Suspended"
                            : driver.isVerified
                            ? "Verified"
                            : "Unverified"
                        }
                      />
                      {licence && (
                        <StatusBadge status={licence.tone} label={licence.label} dot={false} />
                      )}
                    </span>

                    <div className="superPersonActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/tripHistory/${encodeURIComponent(
                            driver.fullName || ""
                          )}/${encodeURIComponent(driver.phoneNumber || "")}`)
                        }
                        title="Trip history"
                      >
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/editDriver/${driver._id}`)
                        }
                        title="Edit driver"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDriverDetail(driver)}
                        title="Driver details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(driverDetail)}
        onClose={() => setDriverDetail(null)}
        title="Driver details"
        subtitle={driverDetail ? driverDetail.fullName : ""}
        size="lg"
      >
        {driverDetail && (
          <React.Fragment>
            <DriverDetails driver={driverDetail} />
            <div className="superPersonFooter">
              {driverDetail.state !== "SUSPENDED" && (
                <Button variant="dangerQuiet" onClick={() => setSuspendTarget([driverDetail])}>
                  Suspend
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() =>
                  (window.location.href = `${endPoint}/superAdmin/editDriver/${driverDetail._id}`)
                }
              >
                Edit
              </Button>
            </div>
          </React.Fragment>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(suspendTarget)}
        onClose={() => (working ? null : setSuspendTarget(null))}
        title={suspendTarget && suspendTarget.length > 1 ? "Suspend drivers" : "Suspend driver"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setSuspendTarget(null), disabled: working },
          { label: "Suspend", onClick: runSuspend, variant: "danger", loading: working },
        ]}
      >
        <div className="superPersonConfirm">
          {suspendTarget && suspendTarget.length > 1
            ? `${suspendTarget.length} drivers will go offline immediately and cannot accept new trips.`
            : "This driver will go offline immediately and cannot accept new trips."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Drivers;
